import { Link } from 'react-router-dom';

export interface FinalCTAContent {
  eyebrow?: string;
  headlineLines: string[];
  subheadline: string;
  primaryCTA: { text: string; link: string };
  secondaryCTA: { text: string; href: string };
}

interface Props {
  content: FinalCTAContent;
}

const FinalCTA = ({ content }: Props) => {
  const { eyebrow, headlineLines, subheadline, primaryCTA, secondaryCTA } = content;

  return (
    <section className="relative py-48 overflow-hidden">

      {/* ── CINEMATIC BACKGROUND ──────────────────────── */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/54c7eb7ea0fcd621a68328a8e02d96fe.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
          style={{ filter: 'brightness(0.22) contrast(1.18) saturate(0.48)' }}
        />
      </div>

      {/* Layered dark overlay */}
      <div
        className="absolute inset-0 z-[1]"
        style={{ background: 'linear-gradient(122deg, rgba(4,10,18,0.97) 0%, rgba(6,16,28,0.88) 38%, rgba(8,16,26,0.8) 60%, rgba(4,8,14,0.96) 100%)' }}
      />

      {/* Gold ambient glow */}
      <div
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 65% 72% at 50% 45%, rgba(201,168,76,0.12) 0%, transparent 62%)' }}
      />

      {/* Gold top accent bar */}
      <div className="absolute top-0 left-0 right-0 h-[3px] z-10 bg-gold" />
      <div className="absolute bottom-0 left-0 right-0 h-px z-10" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.25) 30%, rgba(201,168,76,0.25) 70%, transparent)' }} />

      {/* Architectural vertical accents */}
      <div className="absolute left-[6%] top-0 bottom-0 w-px hidden xl:block" style={{ background: 'linear-gradient(180deg, transparent, rgba(201,168,76,0.14) 28%, rgba(201,168,76,0.1) 72%, transparent)' }} />
      <div className="absolute right-[6%] top-0 bottom-0 w-px hidden xl:block" style={{ background: 'linear-gradient(180deg, transparent, rgba(201,168,76,0.14) 28%, rgba(201,168,76,0.1) 72%, transparent)' }} />

      <div className="relative z-10 max-w-[1280px] mx-auto px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-14">

          {/* ── LEFT HEADLINE COLUMN ──────────────────── */}
          <div className="lg:max-w-[600px]">
            {eyebrow && (
              <div className="flex items-center gap-4 mb-9">
                <div className="h-px w-14 bg-gold" />
                <span className="text-gold text-[10px] tracking-[0.36em] uppercase font-sans font-semibold">
                  {eyebrow}
                </span>
              </div>
            )}
            <h2
              className="font-serif text-white font-bold leading-[1.04] mb-7 text-shadow-dark"
              style={{ fontSize: 'clamp(3.2rem, 5.5vw, 5.2rem)', letterSpacing: '-0.01em' }}
            >
              {headlineLines.map((line, i) => (
                <span
                  key={i}
                  className={i === headlineLines.length - 1 ? 'italic text-gold-light block' : 'block'}
                >
                  {line}
                </span>
              ))}
            </h2>
            <div className="flex items-center gap-3 mb-9">
              <div className="h-[3px] w-20 bg-gold rounded-full" />
              <div className="h-[3px] w-7 rounded-full" style={{ background: 'rgba(201,168,76,0.3)' }} />
            </div>
            <p className="text-white font-sans leading-[2.05] max-w-[500px]" style={{ fontSize: 'clamp(15px, 1.4vw, 17px)' }}>
              {subheadline}
            </p>
          </div>

          {/* ── RIGHT ACTIONS COLUMN ──────────────────── */}
          <div className="flex flex-col gap-7 flex-shrink-0 items-start lg:items-end">
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={primaryCTA.link}
                className="relative bg-gold hover:bg-gold-mid text-white font-bold text-[11px] px-12 py-[18px] rounded transition-all duration-300 cursor-pointer whitespace-nowrap tracking-[0.18em] uppercase flex items-center gap-3 group overflow-hidden"
              >
                <span className="relative z-10">{primaryCTA.text}</span>
                <i className="ri-arrow-right-line relative z-10 text-base transition-transform duration-300 group-hover:translate-x-1.5" />
                <span className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
              <a
                href={secondaryCTA.href}
                className="border-2 border-white/25 hover:border-gold/65 text-white/90 hover:text-gold font-bold text-[11px] px-12 py-[17px] rounded transition-all duration-300 cursor-pointer whitespace-nowrap tracking-[0.18em] uppercase flex items-center gap-3 group"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-sm" />
                </div>
                {secondaryCTA.text}
              </a>
            </div>

            {/* Trust items */}
            <div className="flex flex-col gap-3">
              {[
                { icon: 'ri-shield-check-fill', text: 'Licensed & Insured in California' },
                { icon: 'ri-star-fill', text: '500+ Verified 5-Star Google Reviews' },
                { icon: 'ri-alarm-warning-line', text: '24/7 Emergency Response Available' },
                { icon: 'ri-map-pin-2-fill', text: 'San Bernardino & Inland Empire' },
              ].map(item => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-gold text-[13px]`} />
                  </div>
                  <span className="text-white text-[12.5px] tracking-wide font-sans">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Emergency phone link */}
            <div className="pt-4 border-t border-white/[0.07] w-full flex items-center justify-end">
              <a
                href="tel:+19090000000"
                className="flex items-center gap-2.5 text-white/80 hover:text-gold transition-colors cursor-pointer group"
              >
                <div className="w-7 h-7 flex items-center justify-center rounded-full border border-white/25 group-hover:border-gold/38 transition-colors">
                  <i className="ri-phone-line text-xs" />
                </div>
                <span className="text-[11px] font-sans tracking-wide">Emergency Line — Always Available</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
